import { Injectable } from '@angular/core';
import { TecuidamosService } from '@core/services/core/tecuidamos.service';
import { MultiMapHelperService } from '@core/services/helpers/multimap-helper.service';
import { AllFeatureFlags } from '@models/mmobile';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { TeCuidamosUserData, User } from '../../models/user';
import { DocumentCaptureHelperService } from '../helpers/document-capture-helper.service';
import { NotificationHelperService } from '../helpers/notification-helper.service';
import { BaseMailService } from '../http-services/base-mail.service';
import { ContextService } from './context.service';
import { MMobileHelperService } from './mmobile-helper.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private menuZindexSource = new BehaviorSubject<number>(99);
  private unreadMailsSource = new BehaviorSubject<number>(0);

  public _menuReady$ = new Subject<boolean>();
  servicesViewReload$ = new BehaviorSubject<boolean>(false);
  servicesViewReload = this.servicesViewReload$.asObservable();
  availableRating = new BehaviorSubject<boolean>(false);
  private availableMultiMap = new BehaviorSubject<boolean>(false);
  private availableTeCuidamos = new BehaviorSubject<boolean>(false);
  private isContactEnable = new BehaviorSubject<boolean>(false);
  pwaHeaderActive = !this.ctx.isApp;
  processActive = new BehaviorSubject(false);
  teCuidamosState: TeCuidamosUserData;

  constructor(
    private ctx: ContextService,
    private mailService: BaseMailService,
    private mmobileHelper: MMobileHelperService,
    private notificationHelper: NotificationHelperService,
    private documentCaptureHelper: DocumentCaptureHelperService,
    private multimapHelper: MultiMapHelperService,
    private teCuidamos: TecuidamosService
  ) {}

  get unreadMails$() {
    return this.unreadMailsSource.asObservable();
  }
  get menuZindex$() {
    return this.menuZindexSource.asObservable();
  }
  get availableRating$() {
    return this.availableRating.asObservable();
  }
  get availableMultiMap$() {
    return this.availableMultiMap.asObservable();
  }
  get availableTeCuidamos$() {
    return this.availableTeCuidamos.asObservable();
  }
  get isMultimapEnable() {
    return this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.MULTIMAP);
  }
  get isContactEnable$() {
    return this.isContactEnable.asObservable();
  }
  get menuReady$() {
    return this._menuReady$.asObservable().pipe(take(1));
  }

  setMenuReady$() {
    this._menuReady$.next(true);
  }

  /** @description Initial state update whenever user access for first time to a private page */
  updateMenuState() {
    this.documentCaptureHelper.updateDocumentCaptureNotifications().subscribe();
    this.multimapHelper.loadNotifications();
    this.notificationHelper.updateGenericNotifications();
    this.updateUnreadMails();
    this.showRatingButton();
    this.showMultiMap();
    this.showTeCuidamos();
    this.showContactForm();
  }

  /** @description Hide/Shows rating button, calls to MMobile */
  showRatingButton() {
    this.availableRating.next(
      this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.AVAILABLE_APP_RATING)
    );
  }

  /** @description Update mail's red dot notifications, calls an http service */
  updateUnreadMails() {
    this.mailService
      .getUnreadMails()
      .subscribe(({ unreadMails }) =>
        this.unreadMailsSource.next(unreadMails < 1 ? 0 : unreadMails)
      );
  }

  private showMultiMap() {
    this.availableMultiMap.next(
      this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.MULTIMAP)
    );
  }

  private showTeCuidamos() {
    this.teCuidamos.getState$().subscribe(state => {
      this.teCuidamosState = state;
      this.availableTeCuidamos.next(this.teCuidamos.isAllowed(this.teCuidamosState?.memberState));
    });
  }

  private showContactForm() {
    this.isContactEnable.next(
      this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.CONTACT_FORM)
    );
  }

  /** @description Update mail's red dot notifications, substract one.
   * This should be called when user visit a mail detail.
   */
  updateUnreadCountOnMailVisited() {
    if (this.unreadMailsSource.getValue() < 1) { return; }

    this.unreadMailsSource.next(this.unreadMailsSource.getValue() - 1);
  }

  /**
   * @description
   * Hack for PWA: hides header menu button when menu panel is open
   */
  hackMenuCosmetic(action: 'OPEN' | 'CLOSE') {
    if (this.ctx.isApp) { return; }

    const [HIDE_Z_INDEX, SHOW_Z_INDEX] = [0, 99];
    this.menuZindexSource.next(action === 'OPEN' ? HIDE_Z_INDEX : SHOW_Z_INDEX);
  }

  /**
   * @description
   * returns first letter of name and second name
   */
  getUserShorthand(user: User) {
    if (!user) { return ''; }
    return (user.name || '')
      .split(' ')
      .slice(0, 2)
      .map(str => str[0] || '')
      .join('')
      .toUpperCase();
  }

}
